/**
 * Waiting — at the pickup or at the drop-off — and the moment it turns into
 * overtime.
 *
 *   - State 2 (arrived at pickup): counts up from the arrival
 *     (`driver_status.date`) and offers "the free waiting is over, overtime
 *     starts now" → `overtime_started` with stage 'pickup'. "Utas a kocsiban"
 *     closes that timer server-side as well. Above it, FreeWaitingBlock
 *     counts DOWN to the moment that button is the right one to press —
 *     from the scheduled pickup time, not from the arrival, so a driver who
 *     turned up early has not spent any of the customer's allowance. The
 *     two clocks measure different things and hand over cleanly: the
 *     countdown stops at zero and turns into a sentence, and only the
 *     overtime clock ticks from then on.
 *   - State 3 (guest in the car): "Várakozás a célnál", the same start/stop
 *     timer with stage 'dropoff'. "Letettem" closes it server-side.
 *
 * A stopped timer can be started again while the stage lasts — each start is
 * a new entry in `driver_overtime`, as the API allows. At the pickup,
 * "Nem jelent meg" hands over to the trip screen's no-show procedure (never
 * a one-tap no-show).
 *
 * AND IT ASKS TWICE WHILE THE FREE WAITING IS STILL RUNNING. A driver who
 * reaches for it with twelve minutes of the customer's allowance left is
 * usually mis-tapping, so the first tap names the number, shakes the button
 * and changes it to "Igen, jelentem"; the second goes through. It is a
 * WARNING, NOT A BLOCK — the passenger may have texted to say they are not
 * coming, and the hard gate is the 60-minute airport rule the server
 * enforces. Once they have confirmed it does not ask again.
 *
 * The shake is decoration. Everything it means is in the sentence above the
 * button and in the button's own new label, so a phone that honours
 * `prefers-reduced-motion` (where the animation is off) loses nothing.
 *
 * On a trip the office has closed (`closed` — no longer Confirmed) the wait
 * is over and the trip takes no more input: nothing new is offered — no
 * start, no restart, no "Nem jelent meg" — and the panel stays only while a
 * timer is still running, so the driver can stop it (that closes a record
 * rather than adding one).
 *
 * Everything is rendered from the trip the API last returned (`driver_status`
 * + `driver_overtime`); the clocks only tick locally between responses.
 * Time only — no rates, no amounts: what overtime is worth is not the
 * driver's screen to show.
 */
import React, { useState } from 'react';
import useNow from '../useNow';
import { formatTime } from '../../../Utils/timezone';
import {
  elapsedSince,
  formatElapsed,
  freeWaiting,
  freeWaitingPhase,
  freeWaitingRemaining,
  isInCar,
  isWaitingAtPickup,
  NO_SHOW_EARLY_COPY,
  noShowIsEarly,
  overtimePhase,
  stageTimerMinutes,
} from '../portalFormat';
import { findRunningTimer } from '../../../Utils/driverOvertime';
import FreeWaitingBlock from './FreeWaitingBlock';
import { ClockIcon } from './Icons';

const COPY = {
  pickup: {
    label: 'Várakozás érkezés óta',
    start: 'Ingyenes várakozás vége · Túlóra indul',
  },
  dropoff: {
    label: 'Várakozás a célnál',
    start: 'Túlóra indul a célnál',
  },
};

const RESTART = 'Új túlóra indítása';

export default function WaitingPanel({
  trip, busy = '', closed = false, driverName = '',
  onStartOvertime, onEndOvertime, onNoShow, earlyCopy = NO_SHOW_EARLY_COPY,
}) {
  // '' nothing asked, 'asked' the warning is up, 'ok' they meant it — and
  // from then on the button goes straight through.
  const [noShowAsk, setNoShowAsk] = useState('');
  let stage = '';
  if (isWaitingAtPickup(trip)) stage = 'pickup';
  else if (isInCar(trip)) stage = 'dropoff';

  const phase = stage ? overtimePhase(trip, stage) : 'none';
  const running = findRunningTimer(trip.driver_overtime);
  // The pickup clock counts the whole wait since arrival; at the drop-off
  // there is no arrival to count from, so only a running timer ticks.
  const now = useNow(stage === 'pickup' || phase === 'running');

  // Only the two waiting states get a panel. What was recorded is listed on
  // the trip screen itself (OvertimeCard).
  if (!stage) return null;
  if (closed && phase !== 'running') return null;

  const copy = COPY[stage];
  // Only worth asking while the allowance is still running (or has not even
  // started); once it has expired there is nothing to warn about.
  const early = noShowAsk !== 'ok' && noShowIsEarly(trip, now);
  const warning = noShowAsk === 'asked' && early;

  const tapNoShow = () => {
    if (early && noShowAsk === '') {
      setNoShowAsk('asked');
      return;
    }
    if (noShowAsk === 'asked') setNoShowAsk('ok');
    onNoShow();
  };
  const since = trip.driver_status?.date;
  const startedAt = running && running.stage === stage ? running.started_at : null;

  return (
    <div
      className={`pp-wait${phase === 'running' ? ' pp-wait--overtime' : ''}`}
      data-stage={stage}
    >
      {/* Free waiting is a PICKUP idea: it is the allowance for a passenger
          who has not come out yet, measured from the time they were given.
          There is no such anchor at the drop-off, so nothing is shown there
          rather than a countdown from a time nobody agreed. */}
      {stage === 'pickup' ? (
        <FreeWaitingBlock
          trip={trip}
          now={now}
          overtimeRunning={phase === 'running'}
          driverName={driverName}
        />
      ) : null}

      <div className="pp-wait__row">
        <span className="pp-wait__label">
          <ClockIcon size={16} />
          {copy.label}
        </span>
        {stage === 'pickup' ? (
          <span className="pp-wait__clock" aria-label="Várakozási idő">
            {since ? formatElapsed(elapsedSince(since, now)) : '—'}
          </span>
        ) : null}
      </div>

      {phase === 'none' ? (
        <button
          type="button"
          className={`pp-btn pp-btn--block pp-btn--tall ${stage === 'pickup' ? 'pp-btn--primary' : 'pp-btn--outline'}`}
          onClick={() => onStartOvertime(stage)}
          disabled={Boolean(busy)}
        >
          {busy === 'overtime_started' ? 'Küldés…' : copy.start}
        </button>
      ) : null}

      {phase === 'running' ? (
        <>
          <div className="pp-wait__overtime" role="timer" aria-label="Túlóra">
            <span className="pp-wait__overtime-label">Túlóra fut</span>
            <span className="pp-wait__overtime-clock">
              {formatElapsed(elapsedSince(startedAt, now))}
            </span>
          </div>
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--block pp-btn--tall"
            onClick={onEndOvertime}
            disabled={Boolean(busy)}
          >
            {busy === 'overtime_ended' ? 'Küldés…' : 'Túlóra vége'}
          </button>
        </>
      ) : null}

      {phase === 'ended' ? (
        <>
          <div className="pp-wait__done">
            {`Túlóra lezárva: ${stageTimerMinutes(trip, stage)} perc`}
          </div>
          <button
            type="button"
            className="pp-btn pp-btn--outline pp-btn--block pp-btn--tall"
            onClick={() => onStartOvertime(stage)}
            disabled={Boolean(busy)}
          >
            {busy === 'overtime_started' ? 'Küldés…' : RESTART}
          </button>
        </>
      ) : null}

      {stage === 'pickup' && onNoShow && !closed ? (
        <>
          {/*
            The number, not "too early": a driver deciding whether to keep
            waiting needs the minutes. Carries the whole meaning on its own,
            so the shake below is decoration.
          */}
          {warning ? (
            <p className="pp-wait__warn" role="alert" data-testid="no-show-early-warning">
              {freeWaitingPhase(trip, now) === 'early'
                ? earlyCopy.early(formatTime(freeWaiting(trip)?.starts_at))
                : earlyCopy.running(formatElapsed(freeWaitingRemaining(trip, now)))}
            </p>
          ) : null}
          <button
            type="button"
            className={`pp-btn pp-btn--outline pp-btn--block pp-wait__noshow${warning ? ' pp-shake' : ''}`}
            onClick={tapNoShow}
            disabled={Boolean(busy)}
            data-testid="portal-no-show"
          >
            {warning ? earlyCopy.confirm : 'Nem jelent meg'}
          </button>
        </>
      ) : null}
    </div>
  );
}
